import type { UserRole } from '@delivery-metrics/shared';
import { useMemo } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { useDashboardContext, useData } from '../contexts/DataContext';

import {
  useDeveloperMetrics,
  useLeadershipMetrics,
  usePrograms,
  useRepositories,
  useScrumMasterMetrics,
  useTeams,
} from './useDashboardData';
import { useOfflineSync } from './useOfflineSync';

/**
 * Comprehensive hook that provides all dashboard data and functionality
 * based on the current user's role and global filters
 */
export function useDashboard() {
  const { user, hasRole } = useAuth();
  const {
    globalFilters,
    isRealTimeConnected,
    realTimeError,
    refreshAllData,
    isRefreshing,
    hasErrors,
    clearErrors,
    retryFailedQueries,
    lastUpdated,
  } = useData();

  const { filters } = useDashboardContext();
  const { isOnline, hasOfflineData, isSyncing } = useOfflineSync();

  // Fetch dashboard metrics based on user role
  const leadershipQuery = useLeadershipMetrics(filters);
  const scrumMasterQuery = useScrumMasterMetrics(filters);
  const developerQuery = useDeveloperMetrics(filters);

  // Fetch organization data
  const teamsQuery = useTeams();
  const programsQuery = usePrograms();
  const repositoriesQuery = useRepositories();

  // Determine which dashboard data to return based on user role
  const dashboardData = useMemo(() => {
    if (!user) return null;

    const data: Record<string, any> = {
      Leadership: leadershipQuery.data,
      ScrumMaster: scrumMasterQuery.data,
      Developer: developerQuery.data,
    };

    // Return data for all roles the user has access to
    const userDashboards = user.roles.reduce(
      (acc, role) => {
        acc[role] = data[role];
        return acc;
      },
      {} as Record<string, any>
    );

    return userDashboards;
  }, [user, leadershipQuery.data, scrumMasterQuery.data, developerQuery.data]);

  // Determine loading state based on user roles
  const isLoading = useMemo(() => {
    if (!user) return false;

    const queries: Record<
      string,
      typeof leadershipQuery | typeof scrumMasterQuery | typeof developerQuery
    > = {
      Leadership: leadershipQuery,
      ScrumMaster: scrumMasterQuery,
      Developer: developerQuery,
    };

    return user.roles.some(role => queries[role]?.isLoading);
  }, [user, leadershipQuery, scrumMasterQuery, developerQuery]);

  // Determine error state based on user roles
  const error = useMemo(() => {
    if (!user) return null;

    const queries: Record<
      string,
      typeof leadershipQuery | typeof scrumMasterQuery | typeof developerQuery
    > = {
      Leadership: leadershipQuery,
      ScrumMaster: scrumMasterQuery,
      Developer: developerQuery,
    };

    for (const role of user.roles) {
      const query = queries[role];
      if (query?.error) {
        return query.error;
      }
    }

    return null;
  }, [user, leadershipQuery, scrumMasterQuery, developerQuery]);

  // Organization data
  const organizationData = useMemo(
    () => ({
      teams: teamsQuery.data || [],
      programs: programsQuery.data || [],
      repositories: repositoriesQuery.data || [],
    }),
    [teamsQuery.data, programsQuery.data, repositoriesQuery.data]
  );

  const organizationLoading =
    teamsQuery.isLoading ||
    programsQuery.isLoading ||
    repositoriesQuery.isLoading;
  const organizationError =
    teamsQuery.error || programsQuery.error || repositoriesQuery.error;

  // Connection status
  const connectionStatus = useMemo(
    () => ({
      isOnline,
      isRealTimeConnected,
      realTimeError,
      hasOfflineData,
      isSyncing,
    }),
    [isOnline, isRealTimeConnected, realTimeError, hasOfflineData, isSyncing]
  );

  // Data freshness indicators
  const dataFreshness = useMemo(() => {
    const now = new Date();
    const staleThreshold = 5 * 60 * 1000; // 5 minutes

    return {
      lastUpdated,
      isStale: lastUpdated
        ? now.getTime() - lastUpdated.getTime() > staleThreshold
        : true,
      minutesSinceUpdate: lastUpdated
        ? Math.floor((now.getTime() - lastUpdated.getTime()) / 60000)
        : null,
    };
  }, [lastUpdated]);

  // Refresh functions
  const refresh = {
    all: refreshAllData,
    leadership: () => leadershipQuery.refetch(),
    scrumMaster: () => scrumMasterQuery.refetch(),
    developer: () => developerQuery.refetch(),
    organization: () => {
      teamsQuery.refetch();
      programsQuery.refetch();
      repositoriesQuery.refetch();
    },
  };

  return {
    // User context
    user,
    hasRole,

    // Dashboard data
    dashboardData,
    isLoading,
    error,

    // Organization data
    organizationData,
    organizationLoading,
    organizationError,

    // Filters
    filters: globalFilters,

    // Connection status
    connectionStatus,

    // Data freshness
    dataFreshness,

    // Actions
    refresh,
    clearErrors,
    retryFailedQueries,

    // State flags
    isRefreshing,
    hasErrors,
  };
}

/**
 * Hook for getting specific dashboard data by role
 */
export function useDashboardByRole(role: UserRole) {
  const { user } = useAuth();
  const { filters } = useDashboardContext();

  // Check if user has access to this role
  const hasAccess = user?.roles.includes(role) ?? false;

  // Fetch data based on role
  const leadershipQuery = useLeadershipMetrics(filters);
  const scrumMasterQuery = useScrumMasterMetrics(filters);
  const developerQuery = useDeveloperMetrics(filters);

  const queries = {
    Leadership: leadershipQuery,
    ScrumMaster: scrumMasterQuery,
    Developer: developerQuery,
  };

  const query = queries[role];

  return {
    data: hasAccess ? query.data : null,
    isLoading: hasAccess ? query.isLoading : false,
    error: hasAccess ? query.error : null,
    refetch: hasAccess ? query.refetch : () => Promise.resolve(),
    hasAccess,
  };
}

/**
 * Hook for getting dashboard metrics with caching and offline support
 */
export function useCachedDashboardData(role: UserRole) {
  const { data, isLoading, error, hasAccess } = useDashboardByRole(role);
  const { isOnline } = useOfflineSync();

  // In a real implementation, you would integrate with the offline cache here
  // For now, we'll just return the regular data

  return {
    data,
    isLoading,
    error,
    hasAccess,
    isOnline,
    isCached: !isOnline && !!data, // Data is cached if we're offline but have data
  };
}
